import type { FC } from 'react';
import { Box, Checkbox as MuiCheckbox, Divider, Typography } from '@mui/material';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import GroupByHypothesisToggle from './GroupByHypothesisToggle';
import SaveFiltersControl from './SaveFiltersControl';
import type { CreativeColumnKey } from '../../types/hypothesis';
import type { SavedCreativeFilterSummaryRow } from '../../utils/savedCreativeFilters';

interface TestCreativesHeaderProps {
  totalCreatives: number;
  hypothesisCount: number;
  groupByHypothesis: boolean;
  onGroupByHypothesisChange: (val: boolean) => void;
  allSelected: boolean;
  someSelected: boolean;
  onToggleSelectAll: () => void;
  columnOrder: CreativeColumnKey[];
  visibilityModel: Record<string, boolean>;
  onToggleColumn: (key: CreativeColumnKey) => void;
  onMoveColumn: (key: CreativeColumnKey, direction: 'up' | 'down') => void;
  onResetColumns: () => void;
  saveFiltersEnabled: boolean;
  saveFiltersHasSaved: boolean;
  saveFiltersDirty: boolean;
  savedAtLabel: string;
  savedFiltersSummaryLines: SavedCreativeFilterSummaryRow[];
  onEnableSaveFilters: () => void;
  onDisableSaveFilters: () => void;
  onUpdateSaveFilters: () => void;
  onClearSaveFilters: () => void;
}

const TestCreativesHeader: FC<TestCreativesHeaderProps> = ({
  totalCreatives,
  hypothesisCount,
  groupByHypothesis,
  onGroupByHypothesisChange,
  allSelected,
  someSelected,
  onToggleSelectAll,
  columnOrder,
  visibilityModel,
  onToggleColumn,
  onMoveColumn,
  onResetColumns,
  saveFiltersEnabled,
  saveFiltersHasSaved,
  saveFiltersDirty,
  savedAtLabel,
  savedFiltersSummaryLines,
  onEnableSaveFilters,
  onDisableSaveFilters,
  onUpdateSaveFilters,
  onClearSaveFilters,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <MuiCheckbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={onToggleSelectAll}
        />
        <Typography variant="body1" fontWeight={500}>
          {groupByHypothesis
            ? `${hypothesisCount} Hypothesis`
            : `${totalCreatives} Creative${totalCreatives !== 1 ? 's' : ''}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SaveFiltersControl
          hasSaved={saveFiltersHasSaved}
          enabled={saveFiltersEnabled}
          isDirty={saveFiltersDirty}
          savedAtLabel={savedAtLabel}
          savedSummaryLines={savedFiltersSummaryLines}
          onEnable={onEnableSaveFilters}
          onDisable={onDisableSaveFilters}
          onUpdate={onUpdateSaveFilters}
          onClear={onClearSaveFilters}
        />
        <Divider orientation="vertical" flexItem sx={{mr: 3, ml: 1}} />
        <GroupByHypothesisToggle
          checked={groupByHypothesis}
          onChange={onGroupByHypothesisChange}
        />
        <ColumnVisibilityMenu
          columnOrder={columnOrder}
          visibilityModel={visibilityModel}
          onToggle={onToggleColumn}
          onMove={onMoveColumn}
          onReset={onResetColumns}
        />
      </Box>
    </Box>
  );
};

export default TestCreativesHeader;
